import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { UserRole } from '@sundkortid/shared';
import { hasMinimumRole, ROLE_HIERARCHY } from '@sundkortid/shared';
import { useAuthStore } from '../stores/auth';

interface RoleGateProps {
  minRole: UserRole;
  maxRole?: UserRole;
  redirectTo?: string;
  children: ReactNode;
}

const roleHome: Record<string, string> = {
  staff: '/staff',
  admin: '/admin',
  super_admin: '/admin',
};

export function RoleGate({ minRole, maxRole, redirectTo, children }: RoleGateProps) {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasMinimumRole(user.role, minRole)) {
    return <Navigate to={redirectTo ?? '/dashboard'} replace />;
  }

  // Redirect higher-role users to their own portal
  if (maxRole) {
    const maxLevel = ROLE_HIERARCHY[maxRole];
    const userLevel = ROLE_HIERARCHY[user.role];
    if (userLevel > maxLevel) {
      return <Navigate to={roleHome[user.role] ?? '/dashboard'} replace />;
    }
  }

  return <>{children}</>;
}
